import { useState, useEffect } from "react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";

interface BulkParsingOverlayProps {
  /** Total number of items (if known) */
  estimatedCount?: number;
  /** Progress callback from parsing function */
  progress?: { completed: number; total: number };
  /** Called when user cancels parsing */
  onCancel: () => void;
  /** Whether cancel is currently possible */
  canCancel?: boolean;
  className?: string;
}

/**
 * BulkParsingOverlay - Full-sheet loading state for bulk parsing (Option B)
 * 
 * UX Spec:
 * - Clear visual indicator that parsing is in progress
 * - Elapsed time counter (honest progress, no fake bars)
 * - Cancel/escape path to return to upload step
 * - Expectation setting: "This can take up to ~1 minute for large batches"
 */
export function BulkParsingOverlay({
  estimatedCount,
  progress,
  onCancel,
  canCancel = true,
  className,
}: BulkParsingOverlayProps) {
  const { t } = useLanguage();
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Elapsed time counter
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Format elapsed time
  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Determine status message based on progress
  const getStatusMessage = () => {
    if (progress && progress.total > 1) {
      return `${t("bulk.processing")} ${progress.completed}/${progress.total}`;
    }
    if (estimatedCount && estimatedCount > 1) {
      return `${t("bulk.analyzing") || "Analyzing"} ~${estimatedCount} ${t("orders.ordersLabel").toLowerCase()}...`;
    }
    return t("bulk.processing") || "Processing...";
  };

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center py-16 px-4 text-center",
        className
      )}
    >
      {/* Animated loader */}
      <div className="relative mb-6">
        <Loader2 className="h-14 w-14 animate-spin text-primary" />
        <div className="absolute inset-0 h-14 w-14 rounded-full border-4 border-primary/20" />
      </div>

      {/* Status heading */}
      <h3 className="text-lg font-semibold mb-2">
        {t("bulk.parsingOrders") || "Parsing orders..."}
      </h3>

      {/* Progress status */}
      <p className="text-sm text-muted-foreground mb-1">
        {getStatusMessage()}
      </p>

      {/* Elapsed time */}
      <p className="text-sm font-mono text-muted-foreground mb-4">
        {formatTime(elapsedSeconds)}
      </p>

      {/* Expectation setting for long operations */}
      {elapsedSeconds > 10 && (
        <p className="text-xs text-muted-foreground max-w-xs mb-6">
          {t("bulk.longOperationHint") || 
            "Large batches can take up to ~1 minute. Please wait..."}
        </p>
      )}

      {/* Cancel button - always visible as escape path */}
      {canCancel && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4 mr-1.5" />
          {t("common.cancel") || "Cancel"}
        </Button>
      )}
    </div>
  );
}
